import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { updateClip } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import type { FootageView } from '../footage/footageView';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { formatTime } from '../timeline/timelineGeometry';
import { trimWindow } from './trimWindow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A step of the arrow keys on the trim bar, in seconds of the footage. */
const KEY_STEP_SEC = 0.25;

/**
 * "Part of the clip" (design b1): the part of the footage that the clip shows, as a yellow window
 * on the whole footage. A drag of the bar (or the arrow keys) moves the part; the clip keeps its
 * place on the song.
 */
const ClipTrimComponent: React.FC<{ clip: MusicVideoClip; view: FootageView | null }> = ({ clip, view }) => {
  const intl = useIntl();
  const { change, readOnly } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  const drag = useRef<{ x: number; inSec: number } | null>(null);
  const total = view?.durationSec ?? null;
  const used = (clip.endSec - clip.startSec) * clip.speed;
  const part = trimWindow(clip.inSec, used, total);
  const setIn = (inSec: number) =>
    change(
      (doc) => updateClip(doc, clip.id, { inSec: Math.max(0, Math.min(inSec, part.maxInSec)) }, limitsFor()),
      `trim:${clip.id}`
    );

  return (
    <div className={styles.column}>
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.partOfClip)}</span>
      <div
        className={styles.trim}
        style={view === null || view.posterUrl === null ? undefined : { backgroundImage: `url("${view.posterUrl}")` }}
        role="slider"
        tabIndex={readOnly ? -1 : 0}
        aria-label={intl.formatMessage(panelMessages.partOfClip)}
        aria-valuemin={0}
        aria-valuemax={part.maxInSec}
        aria-valuenow={clip.inSec}
        onPointerDown={(event) => {
          if (readOnly || total === null) {
            return;
          }
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, inSec: clip.inSec };
        }}
        onPointerMove={(event) => {
          if (drag.current === null || total === null) {
            return;
          }
          const { width } = event.currentTarget.getBoundingClientRect();
          setIn(drag.current.inSec + ((event.clientX - drag.current.x) / Math.max(1, width)) * total);
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onKeyDown={(event) => {
          if (readOnly || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
            return;
          }
          event.preventDefault();
          setIn(clip.inSec + (event.key === 'ArrowLeft' ? -KEY_STEP_SEC : KEY_STEP_SEC));
        }}
      >
        <div className={styles.trimOut} style={{ left: 0, width: `${part.leftPct}%` }} />
        <div className={styles.trimWindow} style={{ left: `${part.leftPct}%`, width: `${part.widthPct}%` }} />
        <div className={styles.trimOut} style={{ left: `${part.leftPct + part.widthPct}%`, right: 0 }} />
      </div>
      <div className={styles.row}>
        <small className={styles.hint}>
          {intl.formatMessage(panelMessages.partFrom, { time: formatTime(clip.inSec, true) })}
        </small>
        <span className={styles.grow} />
        {total !== null && (
          <small className={styles.hint}>
            {intl.formatMessage(panelMessages.partOf, { length: used.toFixed(1), total: total.toFixed(1) })}
          </small>
        )}
      </div>
    </div>
  );
};

export const ClipTrim = TypedMemo(ClipTrimComponent);
